import _ from 'lodash';
import { SelectionAreaWrapper } from '@/shared/ui';
import { eventOffsetPreviewStrip } from '@/entities/strip';
import RhythmEventPreviewStrip from '@/features/event-strip-preview';
import { ID_DIV_STRIP_AFIB } from '@/widgets/beats/ui/helper';
import useAfibStripEvents from './useAfibStripEvents';

export type AfibStripEventsProps = {
  activeButton: any;
  studyId: string;
  profileId: string;
  timezoneOffset: number;
  startReport: any;
  ecgDataMap: any;
};

/**
 * Selectable grid of AFib event thumbnails for the beats tab. Wraps the list in the drag selection area keyed to the selected strip id, and renders one preview strip per holter event using the tab's thumbnail size, gain, high pass and channel settings plus the active and selected highlighting.
 *
 * @param props - The strip list props forwarded to the hook that resolves the events and thumbnail settings.
 * @returns The rendered selection area containing one thumbnail per AFib event.
 */
const AfibStripEvents = ({
  activeButton: activeButtonProp,
  studyId,
  profileId,
  timezoneOffset: timezoneOffsetProp,
  startReport: startReportProp,
  ecgDataMap: ecgDataMapProp,
}: AfibStripEventsProps) => {
  const {
    keyRecoil,
    activeButton,
    timezoneOffset,
    startReport,
    ecgDataMap,
    isLoadingStrip,
    sizeThumbnailConfig,
    channelsThumbnail,
    highPassThumbnail,
    gainThumbnail,
    selectedStrip,
    holterEvents,
    selectionAreaWrapperRef,
    setSelectedEvent,
    handleClickSelectEvent,
    isHasSelected,
  } = useAfibStripEvents({
    activeButton: activeButtonProp,
    studyId,
    profileId,
    timezoneOffset: timezoneOffsetProp,
    startReport: startReportProp,
    ecgDataMap: ecgDataMapProp,
  });

  return (
    <SelectionAreaWrapper
      ref={selectionAreaWrapperRef}
      id={ID_DIV_STRIP_AFIB}
      className="strips-container --min-width-strip-event"
      selectables=".single-strip-event"
      handleSetSelected={setSelectedEvent}
      defaultSelectedId={selectedStrip?.id as any}
      isNoData={holterEvents?.length === 0}
      isLoading={isLoadingStrip}
    >
      {(selected) => (
        <div className="strips-img-container">
          {_.map(holterEvents, (event: any, index) => (
            <RhythmEventPreviewStrip
              key={event.id}
              activeButton={activeButton}
              keyRecoil={keyRecoil}
              index={index}
              id={`beat-${event.id}`}
              isActive={event.id === selectedStrip?.id}
              isSelected={selected.has(`beat-${event.id}`) && isHasSelected}
              event={event}
              ecgDataMap={ecgDataMap}
              highPass={highPassThumbnail}
              gain={gainThumbnail}
              channels={channelsThumbnail}
              width={sizeThumbnailConfig.width}
              height={sizeThumbnailConfig.height}
              customOffset={eventOffsetPreviewStrip}
              timezoneOffset={timezoneOffset}
              startReport={startReport}
              onClickSelectEvent={handleClickSelectEvent}
            />
          ))}
        </div>
      )}
    </SelectionAreaWrapper>
  );
};

export default AfibStripEvents;
